// useMemoryPreloader.tsx (fixed)
'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { Json } from '@/integrations/supabase/types'

interface UserMemory {
  category: string
  key_facts: Record<string, unknown>
  updated_at?: string
  version: number
}

type MemoryRow = {
  category: string
  key_facts: Record<string, unknown>
  updated_at?: string
  version: number
}

interface UserProfile {
  id: string
  name?: string
  age?: number
  age_bracket?: 'under_30' | '30_to_49' | '50_plus'
  gender?: string
  country_code?: string
  preferred_persona?: string
  preferences?: Record<string, unknown>
}

// Smart memory preloader for faster conversation starts
export function useMemoryPreloader() {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [userMemories, setUserMemories] = useState<Record<string, UserMemory>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [lastPreloadTime, setLastPreloadTime] = useState<number>(0)
  const [memoryVersions, setMemoryVersions] = useState<Record<string, string>>({})
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitializedRef = useRef(false)

  // Normalizer helper for profile
  const normalizeProfile = (raw: Record<string, unknown>): UserProfile => {
    if (!raw) return null as unknown as UserProfile
    const gender = (typeof raw.gender === 'string' ? raw.gender : typeof raw.sex === 'string' ? raw.sex : undefined) as 'male' | 'female' | 'unspecified' | undefined
    const age_bracket = (typeof raw.age_bracket === 'string' ? raw.age_bracket :
      (typeof raw.age === 'number'
        ? raw.age < 30
          ? 'under_30'
          : raw.age < 50
            ? '30_to_49'
            : '50_plus'
        : undefined)) as 'under_30' | '30_to_49' | '50_plus' | undefined

    return {
      id: String(raw.id),
      name: typeof raw.name === 'string' ? raw.name : undefined,
      age: typeof raw.age === 'number' ? raw.age : undefined,
      age_bracket,
      gender,
      country_code: typeof raw.country_code === 'string' ? raw.country_code : undefined,
      preferred_persona: typeof raw.preferred_persona === 'string' ? raw.preferred_persona as 'Mentor' | 'Coach' | 'Sidekick' : undefined,
      preferences: typeof raw.preferences === 'object' && raw.preferences !== null ? raw.preferences as Record<string, unknown> : undefined
    }
  }

  const preloadUserData = useCallback(async (forceRefresh = false, signal?: AbortSignal) => {
    const now = Date.now()
    if (!forceRefresh && now - lastPreloadTime < 5 * 60 * 1000) {
      return { userProfile, userMemories }
    }

    // Prevent multiple simultaneous preloads
    if (isLoading) {
      return { userProfile, userMemories }
    }

    // Clear any pending preload
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current)
    }

    setIsLoading(true)
    preloadTimeoutRef.current = null
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) {
        setIsLoading(false)
        return { userProfile: null, userMemories: {} }
      }

      console.log('[PERF] Preloading user data and memories...')
      const startTime = performance.now()

      let profileQuery = supabase
        .from('users')
        .select('id, name, age, age_bracket, gender, country_code, preferred_persona, preferences')
        .eq('id', user.id)
      // .single() // Moved below to ensure abortSignal is called first

      let memoriesQuery = supabase
        .from('user_memories')
        .select('category, key_facts, updated_at, version')
        .eq('user_id', user.id)

      if (signal) {
        profileQuery = profileQuery.abortSignal(signal)
        memoriesQuery = memoriesQuery.abortSignal(signal)
      }

      const [profileResult, memoriesResult] = await Promise.all([
        profileQuery.single(), // .single() is now called after abortSignal
        memoriesQuery
      ])

      const loadTime = performance.now() - startTime
      console.log(`[PERF] User data preloaded in ${loadTime.toFixed(0)}ms`)

      // ... rest of processing ...
      let normProfile = userProfile
      let memoriesMap = userMemories

      // Normalize and set profile
      if (profileResult && !profileResult.error && profileResult.data) {
        normProfile = normalizeProfile(profileResult.data)
        setUserProfile(normProfile)
        try { sessionStorage.setItem('userProfile', JSON.stringify(normProfile)) } catch { }
      }

      // Build object map for memories
      if (memoriesResult && !memoriesResult.error && Array.isArray(memoriesResult.data)) {
        const rows = memoriesResult.data as unknown as MemoryRow[]
        const aliasMap: Record<string, UserMemory> = {}
        for (const memory of rows) {
          aliasMap[memory.category] = {
            category: memory.category,
            key_facts: memory.key_facts || {},
            updated_at: memory.updated_at || undefined,
            version: memory.version
          }
        }

        // Add slug aliases
        Object.entries(aliasMap).forEach(([k, v]) => {
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(k)
          if (!isUuid) {
            const slug = k.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and').replace(/[^a-z0-9-]/g, '-')
            if (slug && !aliasMap[slug]) aliasMap[slug] = v
          }
        })

        memoriesMap = aliasMap
        setUserMemories(memoriesMap)
        try { sessionStorage.setItem('userMemories', JSON.stringify(memoriesMap)) } catch { }

        const versionsMap = rows.reduce((acc, memory) => {
          acc[memory.category] = String(memory.version)
          return acc
        }, {} as Record<string, string>)
        setMemoryVersions(versionsMap)
        try { sessionStorage.setItem('userMemoryVersions', JSON.stringify(versionsMap)) } catch { }
      }

      setLastPreloadTime(now)
      return { userProfile: normProfile, userMemories: memoriesMap }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        console.log('[PERF] Preload aborted')
      } else {
        console.error('Error preloading user data:', error)
      }
      return { userProfile: null, userMemories: {} }
    } finally {
      setIsLoading(false)
    }
  }, [lastPreloadTime, userProfile, userMemories, isLoading])

  // Load from cache on mount only once
  useEffect(() => {
    let mounted = true

    const loadData = async () => {
      try {
        const cachedProfile = sessionStorage.getItem('userProfile')
        const cachedMemories = sessionStorage.getItem('userMemories')
        const cachedVersions = sessionStorage.getItem('userMemoryVersions')

        if (mounted) {
          if (cachedProfile) {
            setUserProfile(JSON.parse(cachedProfile))
          }
          if (cachedMemories) {
            setUserMemories(JSON.parse(cachedMemories))
          }
          if (cachedVersions) {
            setMemoryVersions(JSON.parse(cachedVersions))
          }
        }
      } catch {
        // ignore parse errors
      }

      // Preload fresh data only once if cache is stale or missing
      if (mounted && !hasInitializedRef.current) {
        hasInitializedRef.current = true
        await preloadUserData()
      }
    }

    loadData()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const getMemoryForCategory = useCallback((category: string) => {
    return userMemories[category]?.key_facts || {}
  }, [userMemories])

  const getMemoryVersionForCategory = useCallback((category: string) => {
    return memoryVersions[category] || ''
  }, [memoryVersions])

  const updateMemoryForCategory = useCallback(async (category: string, updates: Record<string, unknown>) => {
    if (!userProfile) return

    // Merge optimistically using functional update to avoid stale reads
    setUserMemories(prev => {
      const existing = prev[category]?.key_facts || {}
      const updatedMemory = { ...existing, ...updates }
      const newMap = {
        ...prev,
        [category]: {
          category,
          key_facts: updatedMemory,
          updated_at: new Date().toISOString()
        } as UserMemory
      }
      try {
        sessionStorage.setItem('userMemories', JSON.stringify(newMap))
      } catch { /* ignore */ }
      return newMap
    })

    // Background update to database
    try {
      await supabase.from('user_memories').upsert({
        user_id: userProfile.id,
        category: category,
        key_facts: updates as unknown as Json
      })

      // Refetch version for this category and update memoryVersions atomically
      const { data: fresh } = await supabase
        .from('user_memories')
        .select('version')
        .eq('user_id', userProfile.id)
        .eq('category', category)
        .single()

      const typedFresh: { version: number } | null = (fresh as unknown as { version: number } | null)
      if (typedFresh?.version) {
        setMemoryVersions(prev => {
          const next = { ...prev, [category]: String(typedFresh.version) }
          try {
            sessionStorage.setItem('userMemoryVersions', JSON.stringify(next))
          } catch { /* ignore */ }
          return next
        })
      }
    } catch (error) {
      console.error('Error updating user memory:', error)
    }
  }, [userProfile])

  return {
    userProfile,
    userMemories,
    isLoading,
    preloadUserData,
    getMemoryForCategory,
    getMemoryVersionForCategory,
    updateMemoryForCategory
  }
}

// Global instance for sharing across components
let globalMemoryPreloader: ReturnType<typeof useMemoryPreloader> | null = null

export function getGlobalMemoryPreloader() {
  return globalMemoryPreloader
}

export function setGlobalMemoryPreloader(preloader: ReturnType<typeof useMemoryPreloader>) {
  globalMemoryPreloader = preloader
}
